import { z } from 'zod';
import { dateField } from './_shared.js';

// ---------------------------------------------------------------------------
// Trade  (from trusstor_dashboard/shared/interfaces/ITrade.ts)
// ---------------------------------------------------------------------------

const translationSchema = z.record(z.string(), z.string());

export const tradeSchema = z
	.object({
		_id: z.string().optional(),
		tradeGroup: z.string(),
		tradeId: z.string(),
		backgroundColor: z.string(),
		sortIndex: z.number(),
		tradeTranslations: translationSchema.optional(),
		tradeGroupTranslations: translationSchema.optional(),
	})
	.passthrough();

export type ITrade = z.infer<typeof tradeSchema>;

// ---------------------------------------------------------------------------
// ProfessionDisplaySettings
// (from trusstor_dashboard/shared/interfaces/IProfessionDisplaySettings.ts)
// ---------------------------------------------------------------------------

export enum ProfessionSettingType {
	TEXT = 'text',
	ICON = 'icon',
}

const professionDisplaySettingsSchema = z
	.object({
		trade: z.enum(ProfessionSettingType).optional(),
		specialty: z.literal(ProfessionSettingType.TEXT).optional(),
		contractor: z.literal(ProfessionSettingType.TEXT).optional(),
	})
	.passthrough();

// ---------------------------------------------------------------------------
// ProfessionProjectProperties
// (from trusstor_dashboard/shared/interfaces/IProfessionProjectProperties.ts)
// ---------------------------------------------------------------------------

const professionProjectPropertiesSchema = z
	.object({
		backgroundColor: z.string(),
		displaySettings: professionDisplaySettingsSchema.optional(),
	})
	.passthrough();

// ---------------------------------------------------------------------------
// BaseProfession
// (from trusstor_dashboard/shared/interfaces/IBaseProfession.ts)
// ---------------------------------------------------------------------------

// Inherits .passthrough() from tradeSchema via .extend() — Zod 4 propagates the catchall mode.
const baseProfessionSchema = tradeSchema.extend({
	projectId: z.string(),
	organizationId: z.string(),
	activeProjects: z.record(z.string(), professionProjectPropertiesSchema),
	contractor: z.string().optional(),
	specialty: z.string().optional(),
});

export type IBaseProfession = z.infer<typeof baseProfessionSchema>;

// ---------------------------------------------------------------------------
// Profession  (from trusstor_dashboard/shared/interfaces/IProfession.ts)
// ---------------------------------------------------------------------------

export const professionSchema = baseProfessionSchema
	.extend({
		_id: z.string(),
		dateDeleted: dateField.optional(),
		dateCreated: dateField,
	})
	.passthrough();

export type IProfession = z.infer<typeof professionSchema>;

// ---------------------------------------------------------------------------
// MinimizedProfession
// (from trusstor_dashboard/shared/interfaces/IMinimizedProfession.ts)
// ---------------------------------------------------------------------------

export const minimizedProfessionSchema = professionSchema
	.pick({
		dateCreated: true,
		_id: true,
		projectId: true,
		tradeGroup: true,
		tradeId: true,
		organizationId: true,
	})
	.passthrough();

export type IMinimizedProfession = z.infer<typeof minimizedProfessionSchema>;

export type IProfessionForDisplay = IMinimizedProfession & Partial<Omit<IProfession, keyof IMinimizedProfession>>;

// ---------------------------------------------------------------------------
// ProfessionWithActiveTags
// (from trusstor_dashboard/shared/interfaces/IProfessionWithActiveTags.ts)
// ---------------------------------------------------------------------------

export const professionWithActiveTagsSchema = minimizedProfessionSchema
	.extend({
		activeTagsIds: z.array(z.string()),
	})
	.passthrough();

/** Generic type — accepts any T that extends IMinimizedProfession. */
export type IProfessionWithActiveTags<T extends IMinimizedProfession> = T & {
	activeTagsIds: string[];
};
